import { useEffect, useState } from 'react'
import { IInfrastructureProject, IInfrastructureProjectObject, InfrastructureProjectType, IStreet } from '../../../../types/project.types'
import classes from './styles.module.scss'
import generalClasses from '../styles.module.scss'
import { useTranslation } from 'react-i18next';
import Field from '../../../../components/field/field.component';
import { isEmpty, t } from '../../../../helpers/utils';
import AdditionalInfo from '../../components/additional-info.component';
import FooterComponent from '../../components/footer.component';
import { useParams } from 'react-router-dom';
import { updateInfrastructureProject, updateStreets } from '../../../../requests/supervisor.request';
import StreetList from '../../components/street-list.component';
import { toast } from 'react-toastify';

interface IProps {
    data: IInfrastructureProjectObject
    loadData: () => void;
}

const baseClass = 'village-road-tab';
const translationKey = 'projects.infrastructure-projects.village-road';

const keys: Array<keyof IInfrastructureProject> = [
    'totalStreetNumber',
    'totalStreetLength',
    'lightenedStreetLength',
]

const streetKeys = [
    'nameRu',
    'nameKz',
    'totalStreetLength',
    'factCondition',
    'repairRequired'
]

const VillageRoadTab = ({ data, loadData }: IProps) => {
    const { tab } = useParams<{ tab: string }>();
    const { i18n: { language } } = useTranslation();
    const [formData, setFormData] = useState<IInfrastructureProjectObject>(data);
    const [errors, setErrors] = useState<Record<string, boolean>>({});
    const { kato } = useParams<{ kato: string }>();

    const [isAddingStreet, setIsAddingStreet] = useState(false);
    const [newStreet, setNewStreet] = useState<IStreet | null>(null);

    const [isAddingFund, setIsAddingFund] = useState<Record<number, boolean>>({});
    const [newFund, setNewFund] = useState<Record<number, { labelRu: string; labelKz: string; value: string }>>({});

    const [isAddingInfo, setIsAddingInfo] = useState(false);
    const [newInfo, setNewInfo] = useState<{ labelRu: string; labelKz: string; valueRu: string; valueKz: string }>({ labelRu: '', labelKz: '', valueRu: '', valueKz: '' });

    const [isAddingText, setIsAddingText] = useState(false);
    const [newText, setNewText] = useState({
        textRu: '',
        textKz: ''
    });

    const handleTotalInputChange = (field: string, value: string | number) => {
        setFormData(prev => {
            const current = prev.infrastructureEntity;

            const numericValue = value === '' ? '' : +value;

            const currentTotal = current.totalStreetLength ?? '';
            const currentLightened = current.lightenedStreetLength ?? '';

            if (field === 'lightenedStreetLength') {
                const updatedLightened =
                    typeof numericValue === 'number'
                        ? Math.min(numericValue, +currentTotal)
                        : numericValue;

                return {
                    ...prev,
                    infrastructureEntity: {
                        ...current,
                        lightenedStreetLength: updatedLightened
                    }
                };
            }

            if (field === 'totalStreetLength') {
                const newTotal =
                    typeof numericValue === 'number' ? numericValue : currentTotal;

                const updatedLightened =
                    currentLightened > newTotal ? newTotal : currentLightened;

                return {
                    ...prev,
                    infrastructureEntity: {
                        ...current,
                        totalStreetLength: numericValue,
                        lightenedStreetLength: updatedLightened
                    }
                };
            }

            return {
                ...prev,
                infrastructureEntity: {
                    ...current,
                    [field]: numericValue
                }
            };
        });
    };

    const getPercentage = () => {
        const { totalStreetLength, lightenedStreetLength } = formData.infrastructureEntity;
        if (!totalStreetLength || !lightenedStreetLength) return 0;
        if (isEmpty(totalStreetLength) || isEmpty(lightenedStreetLength) || +totalStreetLength === 0 || +lightenedStreetLength === 0) {
            return 0;
        }
        return ((+lightenedStreetLength / +totalStreetLength) * 100).toFixed(2) + '%';
    }

    const handleSave = () => {
        if (validateFields()) {
            const body = {
                ...formData.infrastructureEntity,
            }

            updateStreets('VILLAGE_ROAD_STREET', body.streetList).then(() => {
                updateInfrastructureProject(tab as InfrastructureProjectType, body).then(() => {
                    loadData();
                    toast.success(t(`admin.success`, language));
                });
            });
        }
    };

    const validateFields = () => {
        setErrors({});
        const newErrors: Record<string, boolean> = {};
        const { infrastructureEntity } = formData;

        keys.forEach(key => {
            if (isEmpty(infrastructureEntity[key])) {
                newErrors[key] = true;
            }
        });

        if (infrastructureEntity.streetList?.length !== 0) {
            infrastructureEntity.streetList.forEach((street, index) => {
                streetKeys.forEach(key => {
                    if (isEmpty(street[key as keyof IStreet])) {
                        newErrors[`${street.id}-${key}`] = true;
                    }
                });
            })
        }

        // if (!infrastructureEntity.files?.length) {
        //   toast.error('Загрузите фото');
        //   newErrors['files'] = true;
        // }

        setErrors(newErrors);

        return Object.values(newErrors).every(error => !error);
    }

    useEffect(() => {
        if (!data) return;
        setFormData(data);
    }, [data]);

    const renderFields = (lang: 'ru' | 'kz') => {
        const { infrastructureEntity } = formData;
        return <div className={generalClasses[`fields-container`]}>
            <div className={`${generalClasses[`field-block`]} ${generalClasses[`field-block--gray`]}`}>
                {
                    ['totalStreetNumber', 'totalStreetLength', 'lightenedStreetLength', 'lighteningCover'].map(field => {
                        const isDisabled = field === 'lighteningCover'
                        const val = isDisabled ? getPercentage() : infrastructureEntity?.[field as keyof typeof infrastructureEntity];
                        const value = val === '' ? '' : val as string | number;
                        return <Field
                            key={field}
                            label={t(`${translationKey}.${field}`, lang)}
                            value={value ?? ''}
                            onChange={(value) => {
                                handleTotalInputChange(field, value);
                            }}
                            type="number"
                            required
                            error={errors[field]}
                            disabled={isDisabled}
                        />
                    })
                }
            </div>

            <StreetList
                list={infrastructureEntity.streetList}
                setFormData={setFormData}
                suffix={lang === 'kz' ? 'Kz' : 'Ru'}
                isAdding={isAddingStreet}
                setIsAdding={setIsAddingStreet}
                newStreet={newStreet}
                setNewStreet={setNewStreet}
                id={infrastructureEntity.id}
                data={formData}
                isAddingFund={isAddingFund}
                setIsAddingFund={setIsAddingFund}
                newFund={newFund}
                setNewFund={setNewFund}
                parentErrors={errors}
            />

            <AdditionalInfo
                data={formData}
                setData={setFormData}
                lang={lang}
                newText={newText}
                setNewText={setNewText}
                newInfo={newInfo}
                setNewInfo={setNewInfo}
                isAddingText={isAddingText}
                setIsAddingText={setIsAddingText}
                isAddingInfo={isAddingInfo}
                setIsAddingInfo={setIsAddingInfo}
                parentErrors={errors}
            />
        </div>

    }

    if (!data || !formData) return null;
    return (
        kato && <div className={classes[baseClass]}>
            <div className={generalClasses['grid']}>
                {renderFields(language === 'ru' ? 'ru' : 'kz')}
                {renderFields(language === 'ru' ? 'kz' : 'ru')}
            </div>
            {/* <ImageGrid
                kato={+kato}
                imageData={formData.images}
                type={'infrastructure-projects'}
                objectId={formData.infrastructureEntity.id}
                setFormData={setFormData}
            /> */}
            <FooterComponent
                onSave={handleSave}
                validateFields={validateFields}
            />
        </div>
    )
}

export default VillageRoadTab